import { useContext, useMemo, useState } from 'react';
import { GetRoutePermissions } from '../../hooks/firebase';
import firestore from '../../firestore';
import { ChevronDown, Save, Users, Globe, UserPlus, Settings, Edit2, X, Check, Trash2, Loader2 } from 'lucide-react';
import { Checkbox } from '../../shadcn/Checkbox';
import Input from '../../components/Input';
import Select from '../../components/Select';
import { Button } from '../../shadcn/Button';
import Notice from '../../components/Notice';
import { UserData } from '../../hooks/auth';
import { validate_email } from '../../utils/validate';
import { COUNTRIES, COUNTRY_LABELS, ACCESS_TYPES, ACCESS_TYPE_LABELS, API_BASE } from '../../utils/constants';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { GlobalContext } from '../../context/GlobalContext';
import { unsafeAPI } from '../../hooks/api';

type User = {
	email: UserData['email'];
	accessType: string;
	accessRoutes: UserData['access_routes'];
	accessCountries?: UserData['access_countries'];
};

// Helper function to create initial country state
const createCountryState = () => {
	return COUNTRIES.reduce(
		(acc, country) => {
			acc[country] = false;
			return acc;
		},
		{} as Record<(typeof COUNTRIES)[number], boolean>
	);
};

// Helper function to create initial access type state
const createAccessTypeState = () => {
	return ACCESS_TYPES.reduce(
		(acc, accessType) => {
			acc[accessType] = false;
			return acc;
		},
		{} as Record<(typeof ACCESS_TYPES)[number], boolean>
	);
};

// Backend-specific access types for internal user creation (differ from frontend ACCESS_TYPES)
const INTERNAL_ACCESS_TYPES = ['admin', 'manager', 'posventa', 'ata', 'atc', 'pagos', 'finanzas', 'finanzas_limitado', 'marketing'] as const;

const INTERNAL_ACCESS_TYPE_LABELS: Record<(typeof INTERNAL_ACCESS_TYPES)[number], string> = {
	admin: 'Admin',
	manager: 'Manager',
	posventa: 'Posventa',
	ata: 'ATA',
	atc: 'ATC',
	pagos: 'Pagos',
	finanzas: 'Finanzas',
	finanzas_limitado: 'Finanzas limitado',
	marketing: 'Marketing'
};

export default function AccessControl() {
	const routePermissions = GetRoutePermissions();
	const [storeEmail, setStoreEmail] = useState('');
	const [selectedRoute, setSelectedRoute] = useState('');
	const [countries, setCountries] = useState(createCountryState());
	const [isLoading, setIsLoading] = useState(false);
	const [message, setMessage] = useState({ text: '', type: '' });

	// Countries access form state
	const [countryEmail, setCountryEmail] = useState('');
	const [countryAccess, setCountryAccess] = useState(createCountryState());
	const [countryLoading, setCountryLoading] = useState(false);
	const [countryMessage, setCountryMessage] = useState({ text: '', type: '' });

	// User search state
	const [searchEmail, setSearchEmail] = useState('');
	const [searchedUser, setSearchedUser] = useState<User | null>(null);
	const [userNotFound, setUserNotFound] = useState(false);
	const [searchLoading, setSearchLoading] = useState(false);

	// Handle search email change
	const handleSearchEmailChange = (value: string) => {
		setSearchEmail(value);
		setUserNotFound(false);
		setSearchedUser(null);
	};

	// Access filter state
	const [accessFilter, setAccessFilter] = useState('todos');

	// Users with special access state
	const [usersWithAccess, setUsersWithAccess] = useState<
		{ email: string; accessRoutes: { route: string; countries: string[] }[] }[]
	>([]);
	const [loadingUsers, setLoadingUsers] = useState(false);

	// Add new route form state
	const [newRouteName, setNewRouteName] = useState('');
	const [newRouteCountries, setNewRouteCountries] = useState(createCountryState());
	const [newRouteAccess, setNewRouteAccess] = useState(createAccessTypeState());
	const [newRouteLoading, setNewRouteLoading] = useState(false);
	const [newRouteMessage, setNewRouteMessage] = useState({ text: '', type: '' });

	// Expanded sections state
	const [expandedSections, setExpandedSections] = useState({
		users: false,
		countryAccess: false,
		addAccess: false,
		addNewRoute: false,
		createInternalUser: false,
		routePermissions: false
	});

	// Route editing state
	const [editingRoute, setEditingRoute] = useState<string | null>(null);
	const [editRouteCountries, setEditRouteCountries] = useState<Record<(typeof COUNTRIES)[number], boolean>>(createCountryState());
	const [editRouteAccess, setEditRouteAccess] = useState<Record<(typeof ACCESS_TYPES)[number], boolean>>(createAccessTypeState());
	const [editRouteLoading, setEditRouteLoading] = useState(false);
	const [deletingRoute, setDeletingRoute] = useState<string | null>(null);
	const [removingUserAccess, setRemovingUserAccess] = useState<{ route: string; email: string } | null>(null);
	const [confirmDialog, setConfirmDialog] = useState<{
		key: 'deleteRoute' | 'removeUserAccess';
		route: string;
		email?: string;
		title: string;
		message: string;
		confirmAction: () => Promise<true | string>;
	} | null>(null);

	// Create internal user form state
	const { userData, internalCountry } = useContext(GlobalContext);
	const [createUserEmail, setCreateUserEmail] = useState('');
	const [createUserStoreName, setCreateUserStoreName] = useState('');
	const [createUserAccessType, setCreateUserAccessType] = useState('');
	const [createUserCountry, setCreateUserCountry] = useState('');
	const [createUserAccessCountries, setCreateUserAccessCountries] = useState(createCountryState());
	const [createUserIsAdmin, setCreateUserIsAdmin] = useState(false);
	const [createUserIsActive, setCreateUserIsActive] = useState(true);
	const [createUserAccessOtherStores, setCreateUserAccessOtherStores] = useState(false);
	const [createUserLoading, setCreateUserLoading] = useState(false);
	const [createUserMessage, setCreateUserMessage] = useState({ text: '', type: '' });

	// Get admin portal routes in alphabetical order
	const adminRoutes = useMemo(() => {
		if (!routePermissions?.admin_portal) return [];
		return [...routePermissions.admin_portal].sort((a, b) => a.route.localeCompare(b.route));
	}, [routePermissions]);

	const availableRoutes = useMemo(() => {
		if (!adminRoutes.length) return [];
		return adminRoutes.map((route) => ({ label: route.route, value: route.route }));
	}, [adminRoutes]);

	const filteredAdminRoutes = useMemo(() => {
		if (accessFilter === 'todos') return adminRoutes;
		if (accessFilter === 'ninguno') return adminRoutes.filter((route) => !route.access || route.access.length === 0);
		return adminRoutes.filter((route) => route.access.includes(accessFilter));
	}, [adminRoutes, accessFilter]);

	const handleCheckboxChange = (country: string) => {
		setCountries((prev) => ({ ...prev, [country as keyof typeof countries]: !prev[country as keyof typeof countries] }));
	};

	const handleCountryCheckboxChange = (country: string) => {
		setCountryAccess((prev) => ({
			...prev,
			[country as keyof typeof countryAccess]: !prev[country as keyof typeof countryAccess]
		}));
	};

	const handleNewRouteCountryChange = (country: string) => {
		setNewRouteCountries((prev) => ({
			...prev,
			[country as keyof typeof newRouteCountries]: !prev[country as keyof typeof newRouteCountries]
		}));
	};

	const handleNewRouteAccessChange = (access: string) => {
		setNewRouteAccess((prev) => ({
			...prev,
			[access as keyof typeof newRouteAccess]: !prev[access as keyof typeof newRouteAccess]
		}));
	};

	// Toggle all countries handlers
	const toggleAllCountries = () => {
		const allSelected = COUNTRIES.every((country) => countries[country]);
		const newState = createCountryState();
		COUNTRIES.forEach((country) => {
			newState[country] = !allSelected;
		});
		setCountries(newState);
	};

	const toggleAllCountryAccess = () => {
		const allSelected = COUNTRIES.every((country) => countryAccess[country]);
		const newState = createCountryState();
		COUNTRIES.forEach((country) => {
			newState[country] = !allSelected;
		});
		setCountryAccess(newState);
	};

	const toggleAllNewRouteCountries = () => {
		const allSelected = COUNTRIES.every((country) => newRouteCountries[country]);
		const newState = createCountryState();
		COUNTRIES.forEach((country) => {
			newState[country] = !allSelected;
		});
		setNewRouteCountries(newState);
	};

	const toggleSection = (section: keyof typeof expandedSections) => {
		setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));

		// Fetch users with special access when opening route permissions section
		if (section === 'routePermissions' && !expandedSections.routePermissions && usersWithAccess.length === 0) {
			fetchUsersWithSpecialAccess();
		}
	};

	// Route editing handlers
	const startEditingRoute = (route: { route: string; countries: string[]; access: string[] }) => {
		setEditingRoute(route.route);

		// Set countries
		const countriesState = createCountryState();
		route.countries.forEach((country) => {
			if (COUNTRIES.includes(country as (typeof COUNTRIES)[number])) {
				countriesState[country as (typeof COUNTRIES)[number]] = true;
			}
		});
		setEditRouteCountries(countriesState);

		// Set access types
		const accessState = createAccessTypeState();
		route.access.forEach((access) => {
			if (ACCESS_TYPES.includes(access as (typeof ACCESS_TYPES)[number])) {
				accessState[access as (typeof ACCESS_TYPES)[number]] = true;
			}
		});
		setEditRouteAccess(accessState);
	};

	const cancelEditingRoute = () => {
		setEditingRoute(null);
		setEditRouteCountries(createCountryState());
		setEditRouteAccess(createAccessTypeState());
	};

	const handleEditRouteCountryChange = (country: string) => {
		setEditRouteCountries((prev) => ({
			...prev,
			[country as keyof typeof editRouteCountries]: !prev[country as keyof typeof editRouteCountries]
		}));
	};

	const handleEditRouteAccessChange = (access: string) => {
		setEditRouteAccess((prev) => ({
			...prev,
			[access as keyof typeof editRouteAccess]: !prev[access as keyof typeof editRouteAccess]
		}));
	};

	const saveEditedRoute = async (routeName: string) => {
		setEditRouteLoading(true);

		try {
			const selectedCountries = Object.keys(editRouteCountries).filter(
				(key) => editRouteCountries[key as keyof typeof editRouteCountries]
			);
			const selectedAccess = Object.keys(editRouteAccess).filter((key) => editRouteAccess[key as keyof typeof editRouteAccess]);

			// Get current route permissions
			const routePermissionsDoc = await firestore.collection('AppSetting').doc('RoutePermissions').get();

			if (!routePermissionsDoc.exists) {
				console.error('Route permissions document not found');
				setEditRouteLoading(false);
				return;
			}

			const currentData = routePermissionsDoc.data();
			const adminPortal = currentData?.admin_portal || [];

			// Find and update the route
			const updatedAdminPortal = adminPortal.map((route: any) => {
				if (route.route === routeName) {
					return {
						...route,
						countries: selectedCountries,
						access: selectedAccess
					};
				}
				return route;
			});

			// Update in Firebase
			await firestore.collection('AppSetting').doc('RoutePermissions').update({
				admin_portal: updatedAdminPortal
			});

			// Reset editing state
			setEditingRoute(null);
			setEditRouteCountries(createCountryState());
			setEditRouteAccess(createAccessTypeState());
		} catch (error) {
			console.error('Error updating route:', error);
		} finally {
			setEditRouteLoading(false);
		}
	};

	const deleteRoute = async (routeName: string) => {
		setDeletingRoute(routeName);

		try {
			const routePermissionsDoc = await firestore.collection('AppSetting').doc('RoutePermissions').get();

			if (!routePermissionsDoc.exists) {
				console.error('Route permissions document not found');
				setDeletingRoute(null);
				return;
			}

			const currentData = routePermissionsDoc.data();
			const adminPortal = currentData?.admin_portal || [];

			const filteredAdminPortal = adminPortal.filter((route: any) => route.route !== routeName);

			await firestore.collection('AppSetting').doc('RoutePermissions').update({
				admin_portal: filteredAdminPortal
			});

			if (editingRoute === routeName) {
				cancelEditingRoute();
			}

			if (expandedSections.routePermissions) {
				fetchUsersWithSpecialAccess();
			}
		} catch (error) {
			console.error('Error deleting route:', error);
		} finally {
			setDeletingRoute(null);
			if (confirmDialog?.key === 'deleteRoute' && confirmDialog?.route === routeName) {
				setConfirmDialog(null);
			}
		}
	};

	const handleDeleteRoute = (routeName: string) => {
		if (deletingRoute || confirmDialog) return;
		setConfirmDialog({
			key: 'deleteRoute',
			route: routeName,
			title: 'Eliminar regla de acceso',
			message: `¿Eliminar la regla de acceso para "${routeName}"? Esta acción no se puede deshacer.`,
			confirmAction: async () => {
				await deleteRoute(routeName);
				return true;
			}
		});
	};

	// Fetch all users with special access routes
	const fetchUsersWithSpecialAccess = async () => {
		setLoadingUsers(true);
		try {
			const usersSnapshot = await firestore.collection('Stores').get();
			const users = usersSnapshot.docs
				.map((doc) => ({
					email: doc.id,
					accessRoutes: (doc.data() as UserData).access_routes || []
				}))
				.filter((user) => user.accessRoutes.length > 0);

			setUsersWithAccess(users);
		} catch (error) {
			console.error('Error fetching users with special access:', error);
		} finally {
			setLoadingUsers(false);
		}
	};

	const removeUserAccessFromRoute = async (routeName: string, email: string) => {
		setRemovingUserAccess({ route: routeName, email });
		try {
			const userRef = firestore.collection('Stores').doc(email);
			const userDoc = await userRef.get();

			if (!userDoc.exists) {
				console.error('User not found when removing access');
				setRemovingUserAccess(null);
				return;
			}

			const userData = userDoc.data() as UserData;
			const accessRoutes = userData.access_routes || [];
			const filteredRoutes = accessRoutes.filter((route) => route.route !== routeName);

			await userRef.update({
				access_routes: filteredRoutes
			});

			setUsersWithAccess((prev) =>
				prev.map((user) =>
					user.email === email
						? {
								...user,
								accessRoutes: filteredRoutes
							}
						: user
				)
			);
		} catch (error) {
			console.error('Error removing user access from route:', error);
		} finally {
			setRemovingUserAccess(null);
			if (confirmDialog?.key === 'removeUserAccess' && confirmDialog?.route === routeName && confirmDialog?.email === email) {
				setConfirmDialog(null);
			}
		}
	};

	const handleRemoveUserAccess = (routeName: string, email: string) => {
		if (removingUserAccess || confirmDialog) return;
		setConfirmDialog({
			key: 'removeUserAccess',
			route: routeName,
			email,
			title: 'Eliminar acceso especial',
			message: `¿Eliminar el acceso especial de "${email}" para la ruta "${routeName}"?`,
			confirmAction: async () => {
				await removeUserAccessFromRoute(routeName, email);
				return true;
			}
		});
	};

	// Get users with access to a specific route
	const getUsersForRoute = (routeName: string) => {
		return usersWithAccess.filter((user) => user.accessRoutes.some((route) => route.route === routeName));
	};

	// Handle country access form submission
	const handleCountrySubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		// Validate form data
		if (!countryEmail.trim()) {
			setCountryMessage({ text: 'Email es requerido', type: 'error' });
			return;
		}

		const selectedCountries = Object.keys(countryAccess).filter((key) => countryAccess[key as keyof typeof countryAccess]);

		setCountryLoading(true);
		setCountryMessage({ text: '', type: '' });

		try {
			// Get user document
			const userDoc = await firestore.collection('Stores').doc(countryEmail.trim()).get();

			if (!userDoc.exists) {
				setCountryMessage({ text: 'Cuenta no encontrada', type: 'error' });
				setCountryLoading(false);
				return;
			}

			// Update user document with selected countries
			await firestore.collection('Stores').doc(countryEmail.trim()).update({
				access_countries: selectedCountries
			});

			setCountryMessage({
				text: `Países actualizados exitosamente. ${countryEmail} ahora tiene acceso a: ${selectedCountries.join(', ') || userDoc?.data()?.country || 'ningún país'}`,
				type: 'success'
			});
			setCountryEmail('');
			setCountryAccess(createCountryState());
		} catch (error) {
			console.error('Error actualizando países de acceso:', error);
			setCountryMessage({ text: 'Error actualizando países de acceso', type: 'error' });
		} finally {
			setCountryLoading(false);
		}
	};

	// Handle new route form submission
	const handleNewRouteSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		// Validate form data
		if (!newRouteName.trim()) {
			setNewRouteMessage({ text: 'Nombre de ruta es requerido', type: 'error' });
			return;
		}

		const selectedCountries = Object.keys(newRouteCountries).filter(
			(key) => newRouteCountries[key as keyof typeof newRouteCountries]
		);
		const selectedAccess = Object.keys(newRouteAccess).filter((key) => newRouteAccess[key as keyof typeof newRouteAccess]);

		if (selectedCountries.length === 0) {
			setNewRouteMessage({ text: 'Al menos un país debe ser seleccionado', type: 'error' });
			return;
		}

		setNewRouteLoading(true);
		setNewRouteMessage({ text: '', type: '' });

		try {
			// Get current route permissions
			const routePermissionsDoc = await firestore.collection('AppSetting').doc('RoutePermissions').get();

			if (!routePermissionsDoc.exists) {
				setNewRouteMessage({ text: 'Documento de permisos no encontrado', type: 'error' });
				setNewRouteLoading(false);
				return;
			}

			const currentData = routePermissionsDoc.data();
			const adminPortal = currentData?.admin_portal || [];

			// Check if route already exists
			const routeExists = adminPortal.some((route: any) => route.route === newRouteName.trim());
			if (routeExists) {
				setNewRouteMessage({ text: 'Esta ruta ya existe', type: 'error' });
				setNewRouteLoading(false);
				return;
			}

			// Add new route
			const newRoute = {
				route: newRouteName.trim(),
				countries: selectedCountries,
				access: selectedAccess
			};

			await firestore
				.collection('AppSetting')
				.doc('RoutePermissions')
				.update({
					admin_portal: [...adminPortal, newRoute]
				});

			setNewRouteMessage({ text: 'Ruta añadida exitosamente', type: 'success' });
			setNewRouteName('');
			setNewRouteCountries(createCountryState());
			setNewRouteAccess(createAccessTypeState());
		} catch (error) {
			console.error('Error añadiendo nueva ruta:', error);
			setNewRouteMessage({ text: 'Error añadiendo nueva ruta', type: 'error' });
		} finally {
			setNewRouteLoading(false);
		}
	};

	// Search for user by email
	const handleUserSearch = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		if (!searchEmail.trim()) {
			return;
		}

		// Validate email format
		if (!validate_email(searchEmail.trim())) {
			setUserNotFound(true);
			setSearchedUser(null);
			return;
		}

		setSearchLoading(true);
		setUserNotFound(false);
		setSearchedUser(null);

		try {
			const userDoc = await firestore.collection('Stores').doc(searchEmail.trim()).get();

			if (!userDoc.exists) {
				setUserNotFound(true);
				setSearchLoading(false);
				return;
			}

			const userData: UserData = userDoc.data() as UserData;

			setSearchedUser({
				email: userDoc.id,
				accessType: userData.access_type || 'tienda',
				accessRoutes: userData.access_routes || [],
				accessCountries: userData.access_countries || [...COUNTRIES]
			});
		} catch (error) {
			console.error('Error searching for user:', error);
			setUserNotFound(true);
		} finally {
			setSearchLoading(false);
		}
	};

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		// Validate form data
		if (!storeEmail.trim()) {
			setMessage({ text: 'Email es requerido', type: 'error' });
			return;
		}

		if (!selectedRoute) {
			setMessage({ text: 'Página es requerida', type: 'error' });
			return;
		}

		const selectedCountries = Object.keys(countries).filter((key) => countries[key as keyof typeof countries]);
		if (selectedCountries.length === 0) {
			setMessage({ text: 'Al menos un país debe ser seleccionado', type: 'error' });
			return;
		}

		setIsLoading(true);
		setMessage({ text: '', type: '' });

		try {
			// Get user document
			const userDoc = await firestore.collection('Stores').doc(storeEmail).get();

			if (!userDoc.exists) {
				setMessage({ text: 'Cuenta no encontrada', type: 'error' });
				setIsLoading(false);
				return;
			}

			const userData: UserData = userDoc.data() as UserData;
			const accessRoutes = userData.access_routes || [];

			// Check if route already exists for this user
			const routeIndex = accessRoutes.findIndex((r) => r.route === selectedRoute);

			if (routeIndex >= 0) {
				// Update existing route
				accessRoutes[routeIndex].countries = selectedCountries;
			} else {
				// Add new route
				accessRoutes.push({
					route: selectedRoute,
					countries: selectedCountries
				});
			}

			// Update user document
			await firestore.collection('Stores').doc(storeEmail).update({
				access_routes: accessRoutes
			});

			setMessage({ text: 'Acceso actualizado exitosamente', type: 'success' });

			// Refresh users list if route permissions section is open
			if (expandedSections.routePermissions) {
				fetchUsersWithSpecialAccess();
			}
		} catch (error) {
			console.error('Error actualizando acceso:', error);
			setMessage({ text: 'Error actualizando acceso', type: 'error' });
		} finally {
			setIsLoading(false);
		}
	};

	// Create internal user handlers
	const resetCreateUserForm = () => {
		setCreateUserEmail('');
		setCreateUserStoreName('');
		setCreateUserAccessType('');
		setCreateUserCountry('');
		setCreateUserAccessCountries(createCountryState());
		setCreateUserIsAdmin(false);
		setCreateUserIsActive(true);
		setCreateUserAccessOtherStores(false);
	};

	const handleCreateUserSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setCreateUserMessage({ text: '', type: '' });

		if (!createUserEmail.trim() || !validate_email(createUserEmail.trim())) {
			setCreateUserMessage({ text: 'Email inválido', type: 'error' });
			return;
		}
		if (!createUserStoreName.trim()) {
			setCreateUserMessage({ text: 'Nombre de tienda es requerido', type: 'error' });
			return;
		}
		if (!createUserAccessType) {
			setCreateUserMessage({ text: 'Tipo de acceso es requerido', type: 'error' });
			return;
		}
		if (!createUserCountry) {
			setCreateUserMessage({ text: 'País es requerido', type: 'error' });
			return;
		}

		setCreateUserLoading(true);

		try {
			const selectedAccessCountries = Object.keys(createUserAccessCountries).filter(
				(key) => createUserAccessCountries[key as keyof typeof createUserAccessCountries]
			);

			const payload = {
				email: createUserEmail.trim(),
				store_name: createUserStoreName.trim(),
				access_type: createUserAccessType,
				country: createUserCountry,
				access_countries: selectedAccessCountries,
				is_admin: createUserIsAdmin,
				is_active: createUserIsActive,
				access_to_other_stores: createUserAccessOtherStores
			};

			const result = await unsafeAPI(
				'POST',
				`${API_BASE}/administration/create_internal_user/`,
				payload,
				userData,
				internalCountry.get()
			);

			if (!result) {
				setCreateUserMessage({ text: 'Error de conexión. Intente más tarde.', type: 'error' });
				return;
			}

			if (result.error) {
				setCreateUserMessage({ text: result.error.message || 'Error de validación', type: 'error' });
				return;
			}

			if (result.success) {
				setCreateUserMessage({
					text: `Usuario interno creado exitosamente: ${result.data?.email || createUserEmail}`,
					type: 'success'
				});
				resetCreateUserForm();
			} else {
				setCreateUserMessage({ text: 'Error creando usuario. Intente más tarde.', type: 'error' });
			}
		} catch (error) {
			console.error('Error creating internal user:', error);
			setCreateUserMessage({ text: 'Error creando usuario. Intente más tarde.', type: 'error' });
		} finally {
			setCreateUserLoading(false);
		}
	};

	return (
		<>
			<div className="mx-auto w-full max-w-7xl space-y-6 p-6">
				<div className="mb-6 space-y-1">
					<h2 className="text-2xl font-semibold text-neutral-100">Gestión de Permisos de Acceso</h2>
					<p className="text-sm text-neutral-400">Administra y configura los permisos de acceso por usuario y país</p>
				</div>

				{/* User Search Card */}
				<div className="rounded-lg border border-neutral-800 bg-neutral-900/50 transition-all">
					<div
						className="flex cursor-pointer items-center justify-between rounded-lg bg-neutral-900/70 px-6 py-4 transition-all hover:bg-neutral-800/80"
						onClick={() => toggleSection('users')}
					>
						<div className="flex items-center gap-3">
							<div className="rounded-lg bg-blue-500/10 p-2">
								<Users className="h-5 w-5 text-blue-500" />
							</div>
							<h3 className="font-medium text-neutral-100">Buscar permisos de usuario</h3>
						</div>
						<ChevronDown
							className={`h-4 w-4 shrink-0 text-neutral-400 transition-transform duration-200 ${expandedSections.users ? 'rotate-180' : ''}`}
						/>
					</div>
					{expandedSections.users && (
						<div className="space-y-6 p-6">
							{/* Search Form */}
							<form onSubmit={handleUserSearch} className="flex items-end gap-3">
								<div className="flex-1">
									<Input
										floatingLabel="Email del usuario"
										placeholder="Buscar usuario por email"
										value={searchEmail}
										onChange={handleSearchEmailChange}
										clearable={true}
									/>
								</div>
								<Button variant="primary" type="submit" loading={searchLoading} className="!h-12 !w-auto px-6">
									Buscar
								</Button>
							</form>

							{/* Search Results */}
							{userNotFound && (
								<div className="rounded-lg border border-red-800/50 bg-red-900/20 p-6 text-center">
									<div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-red-500/10">
										<Users className="h-6 w-6 text-red-400" />
									</div>
									<h4 className="mb-1 font-medium text-neutral-100">Usuario no encontrado</h4>
									<p className="text-sm text-neutral-400">
										No existe un usuario con el email: <span className="font-medium text-neutral-300">{searchEmail}</span>
									</p>
								</div>
							)}

							{searchedUser && (
								<div className="space-y-4">
									{/* User Header */}
									<div className="rounded-lg border border-neutral-800 bg-gradient-to-br from-blue-500/5 to-purple-500/5 p-5">
										<div className="flex items-center gap-3">
											<div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-500/20">
												<Users className="h-5 w-5 text-blue-400" />
											</div>
											<div>
												<h4 className="font-semibold text-neutral-100">{searchedUser.email}</h4>
												<span className="mt-1 inline-block rounded-full bg-blue-900/40 px-3 py-0.5 text-xs font-medium text-blue-300 light:bg-blue-100 light:text-blue-800">
													{searchedUser.accessType}
												</span>
											</div>
										</div>
									</div>

									{/* Access Countries */}
									<div className="rounded-lg border border-neutral-800 bg-neutral-900/30 p-4">
										<h5 className="mb-3 flex items-center gap-2 text-sm font-medium text-neutral-300">
											<Globe className="h-4 w-4" />
											Países con acceso
										</h5>
										<div className="flex flex-wrap gap-2">
											{(searchedUser.accessCountries || []).map((country, idx) => (
												<span
													key={idx}
													className="rounded-full border border-green-800/50 bg-green-900/30 px-3 py-1 text-xs font-medium text-green-300 light:border-green-300 light:bg-green-100 light:text-green-800"
												>
													{COUNTRY_LABELS[country as (typeof COUNTRIES)[number]] || country}
												</span>
											))}
										</div>
									</div>

									{/* Special Access Routes */}
									<div className="rounded-lg border border-neutral-800 bg-neutral-900/30 p-4">
										<h5 className="mb-3 flex items-center gap-2 text-sm font-medium text-neutral-300">
											<Settings className="h-4 w-4" />
											Rutas con acceso personalizado
										</h5>
										{searchedUser.accessRoutes && searchedUser.accessRoutes.length > 0 ? (
											<div className="space-y-2">
												{searchedUser.accessRoutes.map((route, idx) => {
													const isRemoving =
														removingUserAccess?.route === route.route && removingUserAccess?.email === searchedUser.email;

													return (
														<div key={idx} className="rounded-lg border border-neutral-800 bg-neutral-900/50 p-3">
															<div className="flex items-start justify-between gap-2">
																<div>
																	<div className="mb-2 text-sm font-medium text-neutral-100">{route.route}</div>
																	<div className="flex flex-wrap gap-1">
																		{route.countries?.map((country, cidx) => (
																			<span
																				key={cidx}
																				className="rounded-full bg-neutral-800 px-2 py-0.5 text-xs text-neutral-300"
																			>
																				{COUNTRY_LABELS[country as (typeof COUNTRIES)[number]] || country}
																			</span>
																		))}
																	</div>
																</div>
																<button
																	onClick={() => handleRemoveUserAccess(route.route, searchedUser.email)}
																	className="mt-0.5 shrink-0 rounded px-1.5 py-1 text-[10px] text-red-400 transition-colors hover:bg-red-950/60 disabled:opacity-60"
																	type="button"
																	disabled={isRemoving}
																>
																	{isRemoving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
																</button>
															</div>
														</div>
													);
												})}
											</div>
										) : (
											<div className="rounded-lg border border-dashed border-neutral-800 bg-neutral-900/20 p-4 text-center">
												<p className="text-sm text-neutral-500">Sin rutas personalizadas</p>
											</div>
										)}
									</div>
								</div>
							)}

							{/* Empty State */}
							{!searchedUser && !userNotFound && !searchLoading && (
								<div className="rounded-lg border border-dashed border-neutral-800 bg-neutral-900/20 p-12 text-center">
									<div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-neutral-800/50">
										<Users className="h-8 w-8 text-neutral-500" />
									</div>
									<h4 className="mb-1 font-medium text-neutral-300">Busca un usuario</h4>
									<p className="text-sm text-neutral-500">Ingresa un email para ver sus permisos y configuración de acceso</p>
								</div>
							)}
						</div>
					)}
				</div>

				{/* Country Access Card */}
				<div className="rounded-lg border border-neutral-800 bg-neutral-900/50 transition-all">
					<div
						className="flex cursor-pointer items-center justify-between rounded-lg bg-neutral-900/70 px-6 py-4 transition-all hover:bg-neutral-800/80"
						onClick={() => toggleSection('countryAccess')}
					>
						<div className="flex items-center gap-3">
							<div className="rounded-lg bg-green-500/10 p-2">
								<Globe className="h-5 w-5 text-green-500" />
							</div>
							<h3 className="font-medium text-neutral-100">Editar países de usuarios</h3>
						</div>
						<ChevronDown
							className={`h-4 w-4 shrink-0 text-neutral-400 transition-transform duration-200 ${expandedSections.countryAccess ? 'rotate-180' : ''}`}
						/>
					</div>
					{expandedSections.countryAccess && (
						<div className="p-6">
							<form onSubmit={handleCountrySubmit} className="space-y-6">
								<Input
									floatingLabel="Email de la cuenta"
									placeholder="Ingresa el email de la cuenta"
									value={countryEmail}
									onChange={setCountryEmail}
									clearable={true}
								/>

								<div className="space-y-4">
									<div className="flex items-center justify-between">
										<h4 className="font-medium text-neutral-300">Países</h4>
										<Button variant="ghost" type="button" onClick={toggleAllCountryAccess} className="!h-8 !px-3 !text-xs">
											{COUNTRIES.every((country) => countryAccess[country]) ? 'Deseleccionar todos' : 'Seleccionar todos'}
										</Button>
									</div>
									<div className="grid grid-cols-1 gap-3">
										{COUNTRIES.map((country) => (
											<label
												key={country}
												className="flex cursor-pointer items-center gap-3 rounded-lg border border-neutral-800 bg-gradient-to-r from-neutral-900/50 to-neutral-800/30 p-3 transition-all hover:border-neutral-700"
											>
												<Checkbox checked={countryAccess[country]} onCheckedChange={() => handleCountryCheckboxChange(country)} />
												<span className="text-sm text-neutral-300">{COUNTRY_LABELS[country]}</span>
											</label>
										))}
									</div>
								</div>

								{countryMessage.text && (
									<Notice message={countryMessage.text} variant={countryMessage.type === 'error' ? 'error' : 'success'} />
								)}

								<div className="flex justify-end">
									<Button Icon={<Save className="size-4" />} variant="primary" type="submit" loading={countryLoading}>
										Actualizar países
									</Button>
								</div>
							</form>
						</div>
					)}
				</div>

				{/* Add Access Card - Full Width */}
				<div className="rounded-lg border border-neutral-800 bg-neutral-900/50 transition-all">
					<div
						className="flex cursor-pointer items-center justify-between rounded-lg bg-neutral-900/70 px-6 py-4 transition-all hover:bg-neutral-800/80"
						onClick={() => toggleSection('addAccess')}
					>
						<div className="flex items-center gap-3">
							<div className="rounded-lg bg-orange-500/10 p-2">
								<UserPlus className="h-5 w-5 text-orange-500" />
							</div>
							<h3 className="font-medium text-neutral-100">Añadir acceso específico a usuarios</h3>
						</div>
						<ChevronDown
							className={`h-4 w-4 shrink-0 text-neutral-400 transition-transform duration-200 ${expandedSections.addAccess ? 'rotate-180' : ''}`}
						/>
					</div>
					{expandedSections.addAccess && (
						<div className="p-6">
							<form onSubmit={handleSubmit} className="space-y-6">
								<div className="grid gap-4 md:grid-cols-2">
									<Input
										floatingLabel="Email de la cuenta"
										placeholder="Ingresa el email de la cuenta"
										value={storeEmail}
										onChange={setStoreEmail}
										clearable={true}
									/>

									<Select
										floatingLabel="Página"
										options={availableRoutes}
										defaultValue={selectedRoute}
										onSelectedChange={(value) => setSelectedRoute(value as string)}
										placeholder="Selecciona una página"
									/>
								</div>

								<div className="space-y-4">
									<div className="flex items-center justify-between">
										<h4 className="font-medium text-neutral-300">Países</h4>
										<Button variant="ghost" type="button" onClick={toggleAllCountries} className="!h-8 !px-3 !text-xs">
											{COUNTRIES.every((country) => countries[country]) ? 'Deseleccionar todos' : 'Seleccionar todos'}
										</Button>
									</div>
									<div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
										{COUNTRIES.map((country) => (
											<label
												key={country}
												className="flex cursor-pointer items-center gap-3 rounded-lg border border-neutral-800 bg-gradient-to-r from-neutral-900/50 to-neutral-800/30 p-3 transition-all hover:border-neutral-700"
											>
												<Checkbox checked={countries[country]} onCheckedChange={() => handleCheckboxChange(country)} />
												<span className="text-sm text-neutral-300">{COUNTRY_LABELS[country]}</span>
											</label>
										))}
									</div>
								</div>

								{message.text && <Notice message={message.text} variant={message.type === 'error' ? 'error' : 'success'} />}

								<div className="flex justify-end">
									<Button Icon={<Save className="size-4" />} variant="primary" type="submit" loading={isLoading}>
										Guardar regla de acceso
									</Button>
								</div>
							</form>
						</div>
					)}
				</div>

				{/* Add New Route Card - Full Width */}
				<div className="rounded-lg border border-neutral-800 bg-neutral-900/50 transition-all">
					<div
						className="flex cursor-pointer items-center justify-between rounded-lg bg-neutral-900/70 px-6 py-4 transition-all hover:bg-neutral-800/80"
						onClick={() => toggleSection('addNewRoute')}
					>
						<div className="flex items-center gap-3">
							<div className="rounded-lg bg-cyan-500/10 p-2">
								<Settings className="h-5 w-5 text-cyan-500" />
							</div>
							<h3 className="font-medium text-neutral-100">Añadir nueva ruta al sistema</h3>
						</div>
						<ChevronDown
							className={`h-4 w-4 shrink-0 text-neutral-400 transition-transform duration-200 ${expandedSections.addNewRoute ? 'rotate-180' : ''}`}
						/>
					</div>
					{expandedSections.addNewRoute && (
						<div className="p-6">
							<form onSubmit={handleNewRouteSubmit} className="space-y-6">
								<Input
									floatingLabel="Nombre de la ruta"
									placeholder="Ejemplo: Configuración de promociones"
									value={newRouteName}
									onChange={setNewRouteName}
									clearable={true}
								/>

								<div className="space-y-4">
									<div className="flex items-center justify-between">
										<h4 className="font-medium text-neutral-300">Países</h4>
										<Button variant="ghost" type="button" onClick={toggleAllNewRouteCountries} className="!h-8 !px-3 !text-xs">
											{COUNTRIES.every((country) => newRouteCountries[country]) ? 'Deseleccionar todos' : 'Seleccionar todos'}
										</Button>
									</div>
									<div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
										{COUNTRIES.map((country) => (
											<label
												key={country}
												className="flex cursor-pointer items-center gap-3 rounded-lg border border-neutral-800 bg-gradient-to-r from-neutral-900/50 to-neutral-800/30 p-3 transition-all hover:border-neutral-700"
											>
												<Checkbox
													checked={newRouteCountries[country]}
													onCheckedChange={() => handleNewRouteCountryChange(country)}
												/>
												<span className="text-sm text-neutral-300">{COUNTRY_LABELS[country]}</span>
											</label>
										))}
									</div>
								</div>

								<div className="space-y-4">
									<h4 className="font-medium text-neutral-300">Tipos de Acceso</h4>
									<div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-6">
										{ACCESS_TYPES.map((accessType) => (
											<label
												key={accessType}
												className="flex cursor-pointer items-center gap-3 rounded-lg border border-neutral-800 bg-gradient-to-r from-neutral-900/50 to-neutral-800/30 p-3 transition-all hover:border-neutral-700"
											>
												<Checkbox
													checked={newRouteAccess[accessType]}
													onCheckedChange={() => handleNewRouteAccessChange(accessType)}
												/>
												<span className="text-sm text-neutral-300">{ACCESS_TYPE_LABELS[accessType]}</span>
											</label>
										))}
									</div>
								</div>

								{newRouteMessage.text && (
									<Notice message={newRouteMessage.text} variant={newRouteMessage.type === 'error' ? 'error' : 'success'} />
								)}

								<div className="flex justify-end">
									<Button Icon={<Save className="size-4" />} variant="primary" type="submit" loading={newRouteLoading}>
										Añadir ruta
									</Button>
								</div>
							</form>
						</div>
					)}
				</div>

				{/* Create Internal User Card */}
				<div className="rounded-lg border border-neutral-800 bg-neutral-900/50 transition-all">
					<div
						className="flex cursor-pointer items-center justify-between rounded-lg bg-neutral-900/70 px-6 py-4 transition-all hover:bg-neutral-800/80"
						onClick={() => toggleSection('createInternalUser')}
					>
						<div className="flex items-center gap-3">
							<div className="rounded-lg bg-teal-500/10 p-2">
								<UserPlus className="h-5 w-5 text-teal-500" />
							</div>
							<h3 className="font-medium text-neutral-100">Crear usuario interno</h3>
						</div>
						<ChevronDown
							className={`h-4 w-4 shrink-0 text-neutral-400 transition-transform duration-200 ${expandedSections.createInternalUser ? 'rotate-180' : ''}`}
						/>
					</div>
					{expandedSections.createInternalUser && (
						<div className="p-6">
							<form onSubmit={handleCreateUserSubmit} className="space-y-6">
								{/* Email and Store Name row */}
								<div className="grid gap-4 md:grid-cols-2">
									<Input
										floatingLabel="Email"
										placeholder="Email del usuario interno"
										value={createUserEmail}
										onChange={setCreateUserEmail}
										clearable={true}
									/>
									<Input
										floatingLabel="Nombre de tienda"
										placeholder="Nombre para mostrar"
										value={createUserStoreName}
										onChange={setCreateUserStoreName}
										clearable={true}
									/>
								</div>

								{/* Access Type and Country row */}
								<div className="grid gap-4 md:grid-cols-2">
									<Select
										floatingLabel="Tipo de acceso"
										options={INTERNAL_ACCESS_TYPES.map((type) => ({
											label: INTERNAL_ACCESS_TYPE_LABELS[type],
											value: type
										}))}
										defaultValue={createUserAccessType}
										onSelectedChange={(value) => setCreateUserAccessType(value as string)}
										placeholder="Selecciona tipo de acceso"
									/>
									<Select
										floatingLabel="País"
										options={COUNTRIES.map((country) => ({
											label: COUNTRY_LABELS[country],
											value: country
										}))}
										defaultValue={createUserCountry}
										onSelectedChange={(value) => setCreateUserCountry(value as string)}
										placeholder="Selecciona un país"
									/>
								</div>

								{/* Access Countries multi-select */}
								<div className="space-y-4">
									<div className="flex items-center justify-between">
										<h4 className="font-medium text-neutral-300">Países de acceso (opcional)</h4>
										<Button
											variant="ghost"
											type="button"
											onClick={() => {
												const allSelected = COUNTRIES.every(
													(country) => createUserAccessCountries[country]
												);
												const newState = createCountryState();
												if (!allSelected) {
													COUNTRIES.forEach((country) => {
														newState[country] = true;
													});
												}
												setCreateUserAccessCountries(newState);
											}}
											className="!h-8 !px-3 !text-xs"
										>
											{COUNTRIES.every((country) => createUserAccessCountries[country])
												? 'Deseleccionar todos'
												: 'Seleccionar todos'}
										</Button>
									</div>
									<div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
										{COUNTRIES.map((country) => (
											<label
												key={country}
												className="flex cursor-pointer items-center gap-3 rounded-lg border border-neutral-800 bg-gradient-to-r from-neutral-900/50 to-neutral-800/30 p-3 transition-all hover:border-neutral-700"
											>
												<Checkbox
													checked={createUserAccessCountries[country]}
													onCheckedChange={() =>
														setCreateUserAccessCountries((prev) => ({
															...prev,
															[country]: !prev[country]
														}))
													}
												/>
												<span className="text-sm text-neutral-300">
													{COUNTRY_LABELS[country]}
												</span>
											</label>
										))}
									</div>
								</div>

								{/* Boolean options */}
								<div className="space-y-4">
									<h4 className="font-medium text-neutral-300">Opciones</h4>
									<div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
										<label className="flex cursor-pointer items-center gap-3 rounded-lg border border-neutral-800 bg-gradient-to-r from-neutral-900/50 to-neutral-800/30 p-3 transition-all hover:border-neutral-700">
											<Checkbox
												checked={createUserIsAdmin}
												onCheckedChange={() => setCreateUserIsAdmin((prev) => !prev)}
											/>
											<span className="text-sm text-neutral-300">Es administrador</span>
										</label>
										<label className="flex cursor-pointer items-center gap-3 rounded-lg border border-neutral-800 bg-gradient-to-r from-neutral-900/50 to-neutral-800/30 p-3 transition-all hover:border-neutral-700">
											<Checkbox
												checked={createUserIsActive}
												onCheckedChange={() => setCreateUserIsActive((prev) => !prev)}
											/>
											<span className="text-sm text-neutral-300">Activo</span>
										</label>
										<label className="flex cursor-pointer items-center gap-3 rounded-lg border border-neutral-800 bg-gradient-to-r from-neutral-900/50 to-neutral-800/30 p-3 transition-all hover:border-neutral-700">
											<Checkbox
												checked={createUserAccessOtherStores}
												onCheckedChange={() =>
													setCreateUserAccessOtherStores((prev) => !prev)
												}
											/>
											<span className="text-sm text-neutral-300">Acceso a otras tiendas</span>
										</label>
									</div>
								</div>

								{/* Notice */}
								{createUserMessage.text && (
									<Notice
										message={createUserMessage.text}
										variant={createUserMessage.type === 'error' ? 'error' : 'success'}
									/>
								)}

								{/* Submit button */}
								<div className="flex justify-end">
									<Button
										Icon={<Save className="size-4" />}
										variant="primary"
										type="submit"
										loading={createUserLoading}
										className="!w-auto"
									>
										Crear usuario interno
									</Button>
								</div>
							</form>
						</div>
					)}
				</div>

				{/* Route Permissions Card - Full Width */}
				<div className="rounded-lg border border-neutral-800 bg-neutral-900/50 transition-all">
					<div
						className="flex cursor-pointer items-center justify-between rounded-lg bg-neutral-900/70 px-6 py-4 transition-all hover:bg-neutral-800/80"
						onClick={() => toggleSection('routePermissions')}
					>
						<div className="flex items-center gap-3">
							<div className="rounded-lg bg-purple-500/10 p-2">
								<Settings className="h-5 w-5 text-purple-500" />
							</div>
							<h3 className="font-medium text-neutral-100">Reglas de acceso a páginas</h3>
						</div>
						<ChevronDown
							className={`h-4 w-4 shrink-0 text-neutral-400 transition-transform duration-200 ${expandedSections.routePermissions ? 'rotate-180' : ''}`}
						/>
					</div>
					{expandedSections.routePermissions && (
						<div className="p-6">
							{!routePermissions ? (
								<div className="flex justify-center p-4">
									<span className="text-neutral-400">Cargando permisos...</span>
								</div>
							) : adminRoutes.length === 0 ? (
								<div className="flex justify-center p-4">
									<span className="text-neutral-400">No se encontraron rutas</span>
								</div>
							) : (
								<div className="space-y-6">
									<div className="flex flex-wrap items-center justify-between gap-4 md:flex-nowrap">
										<Select
											className="w-40"
											options={[
												{ label: 'Todos', value: 'todos' },
												...ACCESS_TYPES.map((accessType) => ({
													label: ACCESS_TYPE_LABELS[accessType],
													value: accessType
												})),
												{ label: 'Ninguno', value: 'ninguno' }
											]}
											defaultValue={accessFilter}
											onSelectedChange={(value) => setAccessFilter(value as string)}
											isDefaultValueNull={false}
										/>
										<span className="text-sm text-neutral-400">
											Mostrando {filteredAdminRoutes.length}/{adminRoutes.length} reglas
										</span>
									</div>
									<div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
										{filteredAdminRoutes.map((route, index) => {
											const routeUsers = getUsersForRoute(route.route);
											const isEditing = editingRoute === route.route;

											return (
												<div
													key={index}
													className="rounded-lg border border-neutral-800 bg-gradient-to-r from-neutral-900/50 to-neutral-800/30 p-4 transition-all hover:border-neutral-700"
												>
													<div className="mb-3 flex items-start justify-between gap-2">
														<h4 className="font-medium text-neutral-100">{route.route}</h4>
														{!isEditing && (
															<div className="flex items-center gap-1">
																<button
																	onClick={() => startEditingRoute(route)}
																	className="shrink-0 rounded p-1 text-neutral-400 transition-colors hover:bg-neutral-800 hover:text-neutral-200"
																	title="Editar"
																	type="button"
																>
																	<Edit2 className="h-4 w-4" />
																</button>
																<button
																	onClick={() => handleDeleteRoute(route.route)}
																	className="shrink-0 rounded p-1 text-red-500/70 transition-colors hover:bg-red-950/60 hover:text-red-300 disabled:opacity-50"
																	title="Eliminar"
																	type="button"
																	disabled={deletingRoute === route.route}
																>
																	{deletingRoute === route.route ? (
																		<Loader2 className="h-4 w-4 animate-spin" />
																	) : (
																		<Trash2 className="h-4 w-4" />
																	)}
																</button>
															</div>
														)}
													</div>
													<div className="space-y-3">
														{/* Countries Section */}
														<div>
															<h5 className="mb-2 text-xs font-medium text-neutral-300">Países:</h5>
															{isEditing ? (
																<div className="space-y-1.5">
																	{COUNTRIES.map((country) => (
																		<label
																			key={country}
																			className="flex cursor-pointer items-center gap-2 rounded border border-neutral-800 bg-neutral-900/50 px-2 py-1.5 text-xs transition-all hover:border-neutral-700"
																		>
																			<Checkbox
																				checked={editRouteCountries[country]}
																				onCheckedChange={() => handleEditRouteCountryChange(country)}
																			/>
																			<span className="text-neutral-300">{COUNTRY_LABELS[country]}</span>
																		</label>
																	))}
																</div>
															) : (
																<div className="flex flex-wrap gap-1">
																	{route.countries.length > 0 ? (
																		route.countries.map((country, idx) => (
																			<span key={idx} className="rounded-full bg-neutral-800 px-2 py-1 text-xs text-neutral-300">
																				{country}
																			</span>
																		))
																	) : (
																		<span className="text-xs italic text-neutral-500">No especificados</span>
																	)}
																</div>
															)}
														</div>

														{/* Access Types Section */}
														<div>
															<h5 className="mb-2 text-xs font-medium text-neutral-300">Tipos de Acceso:</h5>
															{isEditing ? (
																<div className="space-y-1.5">
																	{ACCESS_TYPES.map((accessType) => (
																		<label
																			key={accessType}
																			className="flex cursor-pointer items-center gap-2 rounded border border-neutral-800 bg-neutral-900/50 px-2 py-1.5 text-xs transition-all hover:border-neutral-700"
																		>
																			<Checkbox
																				checked={editRouteAccess[accessType]}
																				onCheckedChange={() => handleEditRouteAccessChange(accessType)}
																			/>
																			<span className="text-neutral-300">{ACCESS_TYPE_LABELS[accessType]}</span>
																		</label>
																	))}
																</div>
															) : (
																<div className="flex flex-wrap gap-1">
																	{route.access.length > 0 ? (
																		route.access.map((access, idx) => (
																			<span
																				key={idx}
																				className="rounded-full bg-primary-900/40 px-2 py-1 text-xs text-primary-300"
																			>
																				{access}
																			</span>
																		))
																	) : (
																		<span className="text-xs italic text-neutral-500">No especificados</span>
																	)}
																</div>
															)}
														</div>

														{/* Save/Cancel Buttons when editing */}
														{isEditing && (
															<div className="flex gap-2 pt-2">
																<Button
																	variant="primary"
																	onClick={() => saveEditedRoute(route.route)}
																	loading={editRouteLoading}
																	className="!h-8 flex-1 !text-xs"
																>
																	<Check className="mr-1 h-3 w-3" />
																	Guardar
																</Button>
																<Button
																	variant="ghost"
																	onClick={cancelEditingRoute}
																	disabled={editRouteLoading}
																	className="!h-8 flex-1 !text-xs"
																>
																	<X className="mr-1 h-3 w-3" />
																	Cancelar
																</Button>
															</div>
														)}

														{/* Users with Special Access - Always shown, not editable */}
														{!isEditing && (
															<div>
																<h5 className="mb-2 text-xs font-medium text-neutral-300">Usuarios con acceso especial:</h5>
																{loadingUsers ? (
																	<div className="text-xs text-neutral-500">Cargando...</div>
																) : routeUsers.length > 0 ? (
																	<div className="space-y-1.5">
																		{routeUsers.map((user, idx) => {
																			const userRoute = user.accessRoutes.find((r) => r.route === route.route);
																			const isRemoving =
																				removingUserAccess?.route === route.route && removingUserAccess?.email === user.email;
																			return (
																				<div key={idx} className="rounded-md border border-neutral-800/50 bg-neutral-900/60 p-2">
																					<div className="flex items-start gap-2">
																						<div className="flex-1">
																							<div className="break-all text-xs font-medium text-neutral-200">{user.email}</div>
																							<div className="mt-1 flex flex-wrap gap-1">
																								{userRoute?.countries.map((country, cidx) => (
																									<span
																										key={cidx}
																										className="rounded-full bg-blue-900/40 px-1.5 py-0.5 text-[10px] text-blue-300"
																									>
																										{COUNTRY_LABELS[country as (typeof COUNTRIES)[number]] || country}
																									</span>
																								))}
																							</div>
																						</div>
																						<button
																							onClick={() => handleRemoveUserAccess(route.route, user.email)}
																							className="mt-0.5 shrink-0 rounded px-1.5 py-1 text-[10px] text-red-400 transition-colors hover:bg-red-950/60 disabled:opacity-60"
																							type="button"
																							disabled={isRemoving}
																						>
																							{isRemoving ? (
																								<Loader2 className="h-3 w-3 animate-spin" />
																							) : (
																								<Trash2 className="h-3 w-3" />
																							)}
																						</button>
																					</div>
																				</div>
																			);
																		})}
																	</div>
																) : (
																	<span className="text-xs italic text-neutral-500">Ninguno</span>
																)}
															</div>
														)}
													</div>
												</div>
											);
										})}
									</div>
									{filteredAdminRoutes.length === 0 && (
										<div className="flex justify-center p-8">
											<span className="text-neutral-400">No se encontraron reglas para el tipo de acceso seleccionado.</span>
										</div>
									)}
								</div>
							)}
						</div>
					)}
				</div>
			</div>
			{confirmDialog && (
				<ConfirmDialog
					title={confirmDialog.title}
					message={confirmDialog.message}
					onConfirm={confirmDialog.confirmAction}
					onClose={() => setConfirmDialog(null)}
				/>
			)}
		</>
	);
}
